import { max } from "lodash";

import type { SelectSaleConfigurationValues } from "components/word-processor-field/dialogs/word-processor-select-sale-configuration-dialog.component";
import {
  BATTERY_SUBTYPE,
  CHARGER_SUBTYPE,
  DOME_SUBTYPE,
  EAR_MOLD_SUBTYPE,
  OTHER_SUBTYPE,
  RECEIVER_SUBTYPE,
  REMOTE_SUBTYPE,
  TUBE_SUBTYPE
} from "constants/products";
import {
  ACCESSORY_PRODUCT_TYPE,
  FEE_PRODUCT_TYPE,
  HEARING_AID_PRODUCT_TYPE,
  SERVICE_PRODUCT_TYPE
} from "constants/sales";
import { getProductTypeText } from "features/inventory/products/products-list/products-list.utils";
import type {
  DomeInventoryProductSpecification,
  EarmoldInventoryProductSpecification,
  ReceiverInventoryProductSpecification
} from "services/types/inventory-products.type";
import type { LineItem, Sale } from "services/types/sales.type";
import { formatDateOnlyValue, formatMoneyValue } from "utils/common";
import {
  getLineItemEarDisplay,
  getLineItemSerialRaw
} from "utils/line-item-display.util";

import { getSaleTotals, getSortedItems } from "./utils";

const escapeHtml = (value: string): string =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

export const getConfigurableLineItems = (
  sale: Sale,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const {
    include_hearing_aids,
    include_domes,
    include_receivers,
    include_tubes,
    include_earmolds,
    include_chargers,
    include_remotes,
    include_others,
    include_services_dispensing_fees
  } = saleConfiguration;

  const items: LineItem[] = getSortedItems(
    (sale.items || []).filter((item) => {
      const type = item.product?.master_product?.type || item.product?.type;
      const subtype = item.product?.master_product?.subtype;

      if (include_hearing_aids && type === HEARING_AID_PRODUCT_TYPE) {
        return true;
      } else if (
        include_services_dispensing_fees &&
        (type === SERVICE_PRODUCT_TYPE || type === FEE_PRODUCT_TYPE)
      ) {
        return true;
      } else if (type === ACCESSORY_PRODUCT_TYPE) {
        if (include_receivers && subtype === RECEIVER_SUBTYPE) {
          return true;
        } else if (include_tubes && subtype === TUBE_SUBTYPE) {
          return true;
        } else if (include_earmolds && subtype === EAR_MOLD_SUBTYPE) {
          return true;
        } else if (include_chargers && subtype === CHARGER_SUBTYPE) {
          return true;
        } else if (include_domes && subtype === DOME_SUBTYPE) {
          return true;
        } else if (include_remotes && subtype === REMOTE_SUBTYPE) {
          return true;
        } else if (include_others && subtype === OTHER_SUBTYPE) {
          return true;
        }
      }

      return false;
    })
  );

  return items;
};

export const getConfigurableLineItemValue = (
  item: LineItem,
  header: string,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const type = item.product?.master_product?.type || item.product?.type;
  const subtype = item.product?.master_product?.subtype;

  if (header === "Ear") {
    return getLineItemEarDisplay(item);
  } else if (header === "Product") {
    return getProductTypeText(item.product);
  } else if (header === "Description") {
    return getDescriptionValue(item, type, subtype, saleConfiguration);
  } else if (header === "CPT Code") {
    return (
      item.cpt_code?.code ||
      item.product.cpt_code?.code ||
      item.product?.master_product?.cpt_code?.code ||
      ""
    );
  } else if (header === "Manufacturer") {
    return getManufacturerValue(item, type, subtype, saleConfiguration);
  } else if (header === "Model") {
    return getModelValue(item, type, subtype, saleConfiguration);
  } else if (header === "Color") {
    return getColorValue(item, type, subtype, saleConfiguration);
  } else if (header === "Battery") {
    return getBatteryValue(item, type, subtype, saleConfiguration);
  } else if (header === "Serial #") {
    return getSerialNumberValue(item, type, subtype, saleConfiguration);
  } else if (header === "MFR Warranty Exp") {
    return getMFRWarrantyExpirationDateValue(
      item,
      type,
      subtype,
      saleConfiguration
    );
  } else if (header === "L&D Warranty Exp") {
    return getLNDWarrantyExpirationDateValue(
      item,
      type,
      subtype,
      saleConfiguration
    );
  } else if (header === "Service Plan Exp") {
    return getServicePlanExpValue(item, type, saleConfiguration);
  } else if (header === "L&D Deductible") {
    return getLNDDeductibleValue(item, type, subtype, saleConfiguration);
  } else if (header === "Price") {
    return getPriceValue(item, type, subtype, saleConfiguration);
  } else if (header === "Qty") {
    return getQuantityValue(item, type, subtype, saleConfiguration);
  } else if (header === "Resp.") {
    return getResponsibilityValue(item, type, subtype, saleConfiguration);
  } else if (header === "Material") {
    return getMaterialValue(item, type, subtype, saleConfiguration);
  } else if (header === "Canal Length") {
    return getCanalLengthValue(item, type, subtype, saleConfiguration);
  } else if (header === "Canal Venting") {
    return getCanalVentingValue(item, type, subtype, saleConfiguration);
  } else if (header === "Gain") {
    return getGainValue(item, type, subtype, saleConfiguration);
  } else if (header === "Length") {
    return getLengthValue(item, type, subtype, saleConfiguration);
  } else if (header === "Dome Type") {
    return getDomeTypeValue(item, type, subtype, saleConfiguration);
  } else if (header === "Dome Size") {
    return getDomeSizeValue(item, type, subtype, saleConfiguration);
  }
  return "";
};

const getManufacturerValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const masterProduct = item.product?.master_product;
  const value = masterProduct?.manufacturer?.name ?? "";

  const subtypeShowFlags: Record<number, boolean | undefined> = {
    [DOME_SUBTYPE]: saleConfiguration.domes_show_manufacturer,
    [RECEIVER_SUBTYPE]: saleConfiguration.receivers_show_manufacturer,
    [TUBE_SUBTYPE]: saleConfiguration.tubes_show_manufacturer,
    [EAR_MOLD_SUBTYPE]: saleConfiguration.earmolds_show_manufacturer,
    [BATTERY_SUBTYPE]: saleConfiguration.batteries_show_manufacturer,
    [CHARGER_SUBTYPE]: saleConfiguration.chargers_show_manufacturer,
    [REMOTE_SUBTYPE]: saleConfiguration.remotes_show_manufacturer,
    [OTHER_SUBTYPE]: saleConfiguration.others_show_manufacturer
  };

  if (type === HEARING_AID_PRODUCT_TYPE) {
    return saleConfiguration.hearing_aids_show_manufacturer ? value : "";
  }

  if (
    type === ACCESSORY_PRODUCT_TYPE &&
    subtype !== null &&
    subtype !== undefined
  ) {
    const shouldShow = subtypeShowFlags[subtype];
    return shouldShow ? value : "";
  }

  return "";
};

const getModelValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const masterProduct = item.product?.master_product;
  const value = masterProduct?.model ?? "";

  const subtypeShowFlags: Record<number, boolean | undefined> = {
    [DOME_SUBTYPE]: saleConfiguration.domes_show_model,
    [RECEIVER_SUBTYPE]: saleConfiguration.receivers_show_model,
    [TUBE_SUBTYPE]: saleConfiguration.tubes_show_model,
    [EAR_MOLD_SUBTYPE]: saleConfiguration.earmolds_show_model,
    [BATTERY_SUBTYPE]: saleConfiguration.batteries_show_model,
    [CHARGER_SUBTYPE]: saleConfiguration.chargers_show_model,
    [REMOTE_SUBTYPE]: saleConfiguration.remotes_show_model,
    [OTHER_SUBTYPE]: saleConfiguration.others_show_model
  };

  if (type === HEARING_AID_PRODUCT_TYPE) {
    return saleConfiguration.hearing_aids_show_model ? value : "";
  }

  if (
    type === ACCESSORY_PRODUCT_TYPE &&
    subtype !== null &&
    subtype !== undefined
  ) {
    const shouldShow = subtypeShowFlags[subtype];
    return shouldShow ? value : "";
  }

  return "";
};

const getColorValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const inventoryProduct = item.inventory_product;
  const value = inventoryProduct?.color ?? "";

  // Only subtypes that have color flags in the configuration interface
  const subtypeShowFlags: Record<number, boolean | undefined> = {
    [RECEIVER_SUBTYPE]: saleConfiguration.receivers_show_color,
    [BATTERY_SUBTYPE]: saleConfiguration.batteries_show_color
  };

  if (type === HEARING_AID_PRODUCT_TYPE) {
    return saleConfiguration.hearing_aids_show_color ? value : "";
  }

  if (
    type === ACCESSORY_PRODUCT_TYPE &&
    subtype !== null &&
    subtype !== undefined
  ) {
    const shouldShow = subtypeShowFlags[subtype];
    return shouldShow ? value : "";
  }

  return "";
};

const getBatteryValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const inventoryProduct = item.inventory_product;
  const value = inventoryProduct?.battery ?? "";

  if (type === HEARING_AID_PRODUCT_TYPE) {
    return saleConfiguration.hearing_aids_show_battery ? value : "";
  }

  return "";
};

const getSerialNumberValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const value = getLineItemSerialRaw(item);

  const subtypeShowFlags: Record<number, boolean | undefined> = {
    [DOME_SUBTYPE]: saleConfiguration.domes_show_serial,
    [RECEIVER_SUBTYPE]: saleConfiguration.receivers_show_serial,
    [TUBE_SUBTYPE]: saleConfiguration.tubes_show_serial,
    [EAR_MOLD_SUBTYPE]: saleConfiguration.earmolds_show_serial,
    [BATTERY_SUBTYPE]: saleConfiguration.batteries_show_serial,
    [CHARGER_SUBTYPE]: saleConfiguration.chargers_show_serial,
    [REMOTE_SUBTYPE]: saleConfiguration.remotes_show_serial,
    [OTHER_SUBTYPE]: saleConfiguration.others_show_serial
  };

  if (type === HEARING_AID_PRODUCT_TYPE) {
    return saleConfiguration.hearing_aids_show_serial ? value : "";
  }

  if (
    type === ACCESSORY_PRODUCT_TYPE &&
    subtype !== null &&
    subtype !== undefined
  ) {
    const shouldShow = subtypeShowFlags[subtype];
    return shouldShow ? value : "";
  }

  return "";
};

const getMFRWarrantyExpirationDateValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const inventoryProduct = item.inventory_product;
  const date = inventoryProduct?.manufacturer_warranty_expiration_date ?? "";

  if (!date) return "";

  const value = formatDateOnlyValue(date, "MMM dd, yyyy");

  const subtypeShowFlags: Record<number, boolean | undefined> = {
    [DOME_SUBTYPE]: saleConfiguration.domes_show_mfr_warranty_exp,
    [RECEIVER_SUBTYPE]: saleConfiguration.receivers_show_mfr_warranty_exp,
    [TUBE_SUBTYPE]: saleConfiguration.tubes_show_mfr_warranty_exp,
    [EAR_MOLD_SUBTYPE]: saleConfiguration.earmolds_show_mfr_warranty_exp,
    [BATTERY_SUBTYPE]: saleConfiguration.batteries_show_mfr_warranty_exp,
    [CHARGER_SUBTYPE]: saleConfiguration.chargers_show_mfr_warranty_exp,
    [REMOTE_SUBTYPE]: saleConfiguration.remotes_show_mfr_warranty_exp,
    [OTHER_SUBTYPE]: saleConfiguration.others_show_mfr_warranty_exp
  };

  if (type === HEARING_AID_PRODUCT_TYPE) {
    return saleConfiguration.hearing_aids_show_mfr_warranty_exp ? value : "";
  }

  if (
    type === ACCESSORY_PRODUCT_TYPE &&
    subtype !== null &&
    subtype !== undefined
  ) {
    const shouldShow = subtypeShowFlags[subtype];
    return shouldShow ? value : "";
  }

  return "";
};

const getLNDWarrantyExpirationDateValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const inventoryProduct = item.inventory_product;
  const date = inventoryProduct?.loss_damage_warranty_expiration_date ?? "";

  if (!date) return "";

  const value = formatDateOnlyValue(date, "MMM dd, yyyy");

  const subtypeShowFlags: Record<number, boolean | undefined> = {
    [DOME_SUBTYPE]: saleConfiguration.domes_show_lnd_warranty_exp,
    [RECEIVER_SUBTYPE]: saleConfiguration.receivers_show_lnd_warranty_exp,
    [TUBE_SUBTYPE]: saleConfiguration.tubes_show_lnd_warranty_exp,
    [EAR_MOLD_SUBTYPE]: saleConfiguration.earmolds_show_lnd_warranty_exp,
    [BATTERY_SUBTYPE]: saleConfiguration.batteries_show_lnd_warranty_exp,
    [CHARGER_SUBTYPE]: saleConfiguration.chargers_show_lnd_warranty_exp,
    [REMOTE_SUBTYPE]: saleConfiguration.remotes_show_lnd_warranty_exp,
    [OTHER_SUBTYPE]: saleConfiguration.others_show_lnd_warranty_exp
  };

  if (type === HEARING_AID_PRODUCT_TYPE) {
    return saleConfiguration.hearing_aids_show_lnd_warranty_exp ? value : "";
  }

  if (
    type === ACCESSORY_PRODUCT_TYPE &&
    subtype !== null &&
    subtype !== undefined
  ) {
    const shouldShow = subtypeShowFlags[subtype];
    return shouldShow ? value : "";
  }

  return "";
};

const getServicePlanExpValue = (
  item: LineItem,
  type: number | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  if (type !== HEARING_AID_PRODUCT_TYPE) return "";
  if (!saleConfiguration.hearing_aids_show_service_plan_exp) return "";

  const date = item.patient_products?.[0]?.service_plan_expiration_date ?? "";
  if (!date) return "";

  return formatDateOnlyValue(date, "MMM dd, yyyy");
};

const getLNDDeductibleValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const value = item.product?.loss_and_deductible_amount;
  if (!value || Number(value) === 0) return "";
  const formatted = formatMoneyValue(Number(value));

  const subtypeShowFlags: Record<number, boolean | undefined> = {
    [DOME_SUBTYPE]: saleConfiguration.domes_show_lnd_deductible,
    [RECEIVER_SUBTYPE]: saleConfiguration.receivers_show_lnd_deductible,
    [TUBE_SUBTYPE]: saleConfiguration.tubes_show_lnd_deductible,
    [EAR_MOLD_SUBTYPE]: saleConfiguration.earmolds_show_lnd_deductible,
    [BATTERY_SUBTYPE]: saleConfiguration.batteries_show_lnd_deductible,
    [CHARGER_SUBTYPE]: saleConfiguration.chargers_show_lnd_deductible,
    [REMOTE_SUBTYPE]: saleConfiguration.remotes_show_lnd_deductible,
    [OTHER_SUBTYPE]: saleConfiguration.others_show_lnd_deductible
  };

  if (type === HEARING_AID_PRODUCT_TYPE) {
    return saleConfiguration.hearing_aids_show_lnd_deductible ? formatted : "";
  }

  if (
    type === ACCESSORY_PRODUCT_TYPE &&
    subtype !== null &&
    subtype !== undefined
  ) {
    const shouldShow = subtypeShowFlags[subtype];
    return shouldShow ? formatted : "";
  }

  return "";
};

const renderDiscountedPriceHtml = (
  item: LineItem,
  quantity: number
): string => {
  const unitPrice = Number(item.price || 0);
  const unitDiscount = Number(item.discount || 0);

  if (unitDiscount <= 0) {
    return formatMoneyValue(unitPrice * quantity);
  }

  const originalAmount = unitPrice * quantity;
  const discountedAmount = Math.max(unitPrice - unitDiscount, 0) * quantity;
  const percent =
    unitPrice > 0
      ? Math.round((unitDiscount / unitPrice) * 100 * 100) / 100
      : null;
  const label = item.discount_type?.title || "Discount";
  const caption = percent !== null ? `${label} (${percent}%)` : label;

  return [
    `<s style="color:#757575;font-size:11px;">${formatMoneyValue(originalAmount)}</s>`,
    `<strong>${formatMoneyValue(discountedAmount)}</strong>`,
    `<span style="color:#757575;font-size:11px;">${caption}</span>`
  ].join("<br>");
};

const getPriceValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const quantity = Number(item.quantity || 1);
  const value = renderDiscountedPriceHtml(item, quantity);

  const subtypeShowFlags: Record<number, boolean | undefined> = {
    [DOME_SUBTYPE]: saleConfiguration.domes_show_price,
    [RECEIVER_SUBTYPE]: saleConfiguration.receivers_show_price,
    [TUBE_SUBTYPE]: saleConfiguration.tubes_show_price,
    [EAR_MOLD_SUBTYPE]: saleConfiguration.earmolds_show_price,
    [BATTERY_SUBTYPE]: saleConfiguration.batteries_show_price,
    [CHARGER_SUBTYPE]: saleConfiguration.chargers_show_price,
    [REMOTE_SUBTYPE]: saleConfiguration.remotes_show_price,
    [OTHER_SUBTYPE]: saleConfiguration.others_show_price
  };

  if (type === HEARING_AID_PRODUCT_TYPE) {
    return saleConfiguration.hearing_aids_show_price ? value : "";
  }

  if (
    type === ACCESSORY_PRODUCT_TYPE &&
    subtype !== null &&
    subtype !== undefined
  ) {
    const shouldShow = subtypeShowFlags[subtype];
    return shouldShow ? value : "";
  } else if (
    (type === SERVICE_PRODUCT_TYPE || FEE_PRODUCT_TYPE) &&
    saleConfiguration.services_show_price
  ) {
    return value ?? "";
  }

  return "";
};

const getResponsibilityValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  return [
    getPTResponsibilityValue(item, type, subtype, saleConfiguration),
    getINSResponsibilityValue(item, type, subtype, saleConfiguration),
    getMCPResponsibilityValue(item, type, subtype, saleConfiguration)
  ]
    .filter((item) => item !== "")
    .join("<br />");
};

const getPTResponsibilityValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const value = `PT: ${formatMoneyValue(Number(item.patient_responsibility || "0"))}`;

  const subtypeShowFlags: Record<number, boolean | undefined> = {
    [DOME_SUBTYPE]: saleConfiguration.domes_show_patient_resp,
    [RECEIVER_SUBTYPE]: saleConfiguration.receivers_show_patient_resp,
    [TUBE_SUBTYPE]: saleConfiguration.tubes_show_patient_resp,
    [EAR_MOLD_SUBTYPE]: saleConfiguration.earmolds_show_patient_resp,
    [BATTERY_SUBTYPE]: saleConfiguration.batteries_show_patient_resp,
    [CHARGER_SUBTYPE]: saleConfiguration.chargers_show_patient_resp,
    [REMOTE_SUBTYPE]: saleConfiguration.remotes_show_patient_resp,
    [OTHER_SUBTYPE]: saleConfiguration.others_show_patient_resp
  };

  if (type === HEARING_AID_PRODUCT_TYPE) {
    return saleConfiguration.hearing_aids_show_patient_resp ? value : "";
  }

  if (
    type === ACCESSORY_PRODUCT_TYPE &&
    subtype !== null &&
    subtype !== undefined
  ) {
    const shouldShow = subtypeShowFlags[subtype];
    return shouldShow ? value : "";
  }

  return "";
};

const getINSResponsibilityValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const value = `INS: ${formatMoneyValue(Number(item.insurer_responsibility || "0"))}`;

  const subtypeShowFlags: Record<number, boolean | undefined> = {
    [DOME_SUBTYPE]: saleConfiguration.domes_show_insurance_resp,
    [RECEIVER_SUBTYPE]: saleConfiguration.receivers_show_insurance_resp,
    [TUBE_SUBTYPE]: saleConfiguration.tubes_show_insurance_resp,
    [EAR_MOLD_SUBTYPE]: saleConfiguration.earmolds_show_insurance_resp,
    [BATTERY_SUBTYPE]: saleConfiguration.batteries_show_insurance_resp,
    [CHARGER_SUBTYPE]: saleConfiguration.chargers_show_insurance_resp,
    [REMOTE_SUBTYPE]: saleConfiguration.remotes_show_insurance_resp,
    [OTHER_SUBTYPE]: saleConfiguration.others_show_insurance_resp
  };

  if (type === HEARING_AID_PRODUCT_TYPE) {
    return saleConfiguration.hearing_aids_show_insurance_resp ? value : "";
  }

  if (
    type === ACCESSORY_PRODUCT_TYPE &&
    subtype !== null &&
    subtype !== undefined
  ) {
    const shouldShow = subtypeShowFlags[subtype];
    return shouldShow ? value : "";
  }

  return "";
};

const getMCPResponsibilityValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  if (item.managed_care_plan === null || item.managed_care_plan === undefined)
    return "";
  const value = `MCP: ${formatMoneyValue(Number(item.managed_care_plan || "0"))}`;

  const subtypeShowFlags: Record<number, boolean | undefined> = {
    [DOME_SUBTYPE]: saleConfiguration.domes_show_managed_care_resp,
    [RECEIVER_SUBTYPE]: saleConfiguration.receivers_show_managed_care_resp,
    [TUBE_SUBTYPE]: saleConfiguration.tubes_show_managed_care_resp,
    [EAR_MOLD_SUBTYPE]: saleConfiguration.earmolds_show_managed_care_resp,
    [BATTERY_SUBTYPE]: saleConfiguration.batteries_show_managed_care_resp,
    [CHARGER_SUBTYPE]: saleConfiguration.chargers_show_managed_care_resp,
    [REMOTE_SUBTYPE]: saleConfiguration.remotes_show_managed_care_resp,
    [OTHER_SUBTYPE]: saleConfiguration.others_show_managed_care_resp
  };

  if (type === HEARING_AID_PRODUCT_TYPE) {
    return saleConfiguration.hearing_aids_show_managed_care_resp ? value : "";
  }

  if (
    type === ACCESSORY_PRODUCT_TYPE &&
    subtype !== null &&
    subtype !== undefined
  ) {
    const shouldShow = subtypeShowFlags[subtype];
    return shouldShow ? value : "";
  }

  return "";
};

const getQuantityValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const value = String(Number(item.quantity || 1));

  const subtypeShowFlags: Record<number, boolean | undefined> = {
    [DOME_SUBTYPE]: saleConfiguration.domes_show_quantity,
    [RECEIVER_SUBTYPE]: saleConfiguration.receivers_show_quantity,
    [TUBE_SUBTYPE]: saleConfiguration.tubes_show_quantity,
    [EAR_MOLD_SUBTYPE]: saleConfiguration.earmolds_show_quantity,
    [BATTERY_SUBTYPE]: saleConfiguration.batteries_show_quantity,
    [CHARGER_SUBTYPE]: saleConfiguration.chargers_show_quantity,
    [REMOTE_SUBTYPE]: saleConfiguration.remotes_show_quantity,
    [OTHER_SUBTYPE]: saleConfiguration.others_show_quantity
  };

  if (type === HEARING_AID_PRODUCT_TYPE) {
    return saleConfiguration.hearing_aids_show_quantity ? value : "";
  }

  if (
    type === ACCESSORY_PRODUCT_TYPE &&
    subtype !== null &&
    subtype !== undefined
  ) {
    const shouldShow = subtypeShowFlags[subtype];
    return shouldShow ? value : "";
  } else if (
    (type === SERVICE_PRODUCT_TYPE || FEE_PRODUCT_TYPE) &&
    saleConfiguration.services_show_quantity
  ) {
    return value ?? "";
  }

  return "";
};

const getMaterialValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const inventoryProduct = item.inventory_product;

  const value =
    (inventoryProduct?.specification as EarmoldInventoryProductSpecification)
      ?.material ??
    (
      item.product?.master_product
        ?.specification as EarmoldInventoryProductSpecification
    )?.material ??
    "";

  if (
    type === ACCESSORY_PRODUCT_TYPE &&
    subtype === EAR_MOLD_SUBTYPE &&
    saleConfiguration.earmolds_show_material
  ) {
    return value;
  }

  return "";
};

const getCanalLengthValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const inventoryProduct = item.inventory_product;

  const value =
    (inventoryProduct?.specification as EarmoldInventoryProductSpecification)
      ?.canal_length ??
    (
      item.product?.master_product
        ?.specification as EarmoldInventoryProductSpecification
    )?.canal_length ??
    "";

  if (
    type === ACCESSORY_PRODUCT_TYPE &&
    subtype === EAR_MOLD_SUBTYPE &&
    saleConfiguration.earmolds_show_canal_length
  ) {
    return value;
  }

  return "";
};

const getCanalVentingValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const inventoryProduct = item.inventory_product;

  const value =
    (inventoryProduct?.specification as EarmoldInventoryProductSpecification)
      ?.venting ??
    (
      item.product?.master_product
        ?.specification as EarmoldInventoryProductSpecification
    )?.venting ??
    "";

  if (
    type === ACCESSORY_PRODUCT_TYPE &&
    subtype === EAR_MOLD_SUBTYPE &&
    saleConfiguration.earmolds_show_canal_venting
  ) {
    return value;
  }

  return "";
};

const getGainValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const inventoryProduct = item.inventory_product;

  const value =
    (inventoryProduct?.specification as ReceiverInventoryProductSpecification)
      ?.gain ??
    (
      item.product?.master_product
        ?.specification as ReceiverInventoryProductSpecification
    )?.gain ??
    "";

  if (
    type === ACCESSORY_PRODUCT_TYPE &&
    subtype === RECEIVER_SUBTYPE &&
    saleConfiguration.receivers_show_gain
  ) {
    return value;
  }

  return "";
};

const getLengthValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const inventoryProduct = item.inventory_product;

  const inventorySpec =
    inventoryProduct?.specification as ReceiverInventoryProductSpecification;
  const masterSpec = item.product?.master_product
    ?.specification as ReceiverInventoryProductSpecification;

  const lengthValue = inventorySpec?.length ?? masterSpec?.length;
  const value = lengthValue ? String(lengthValue) : "";

  if (
    type === ACCESSORY_PRODUCT_TYPE &&
    subtype === RECEIVER_SUBTYPE &&
    saleConfiguration.receivers_show_length
  ) {
    return value;
  }

  return "";
};

const getDomeTypeValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const inventoryProduct = item.inventory_product;

  const value =
    (inventoryProduct?.specification as DomeInventoryProductSpecification)
      ?.type ??
    (
      item.product?.master_product
        ?.specification as DomeInventoryProductSpecification
    )?.type ??
    "";

  if (
    type === ACCESSORY_PRODUCT_TYPE &&
    subtype === DOME_SUBTYPE &&
    saleConfiguration.domes_show_dome_type
  ) {
    return value;
  }

  return "";
};

const getDomeSizeValue = (
  item: LineItem,
  type: number | undefined,
  subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const inventoryProduct = item.inventory_product;

  const value =
    (inventoryProduct?.specification as DomeInventoryProductSpecification)
      ?.size ??
    (
      item.product?.master_product
        ?.specification as DomeInventoryProductSpecification
    )?.size ??
    "";

  if (
    type === ACCESSORY_PRODUCT_TYPE &&
    subtype === DOME_SUBTYPE &&
    saleConfiguration.domes_show_dome_size
  ) {
    return value;
  }

  return "";
};

const getDescriptionValue = (
  item: LineItem,
  type: number | undefined,
  _subtype: number | null | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  if (
    (type === SERVICE_PRODUCT_TYPE || type === FEE_PRODUCT_TYPE) &&
    saleConfiguration.services_show_description
  ) {
    return item.description ?? "";
  }
  return "";
};

const HEADER_ITEMS_ORDER = [
  "Ear",
  "CPT Code",
  "Product",
  "Description",
  "Manufacturer",
  "Model",
  "Color",
  "Battery",
  "Material",
  "Serial #",
  "MFR Warranty Exp",
  "L&D Warranty Exp",
  "Service Plan Exp",
  "L&D Deductible",
  "Price",
  "Qty",
  "Resp.",
  "Canal Length",
  "Canal Venting",
  "Gain",
  "Length",
  "Dome Type",
  "Dome Size"
];

export const getConfigurableLineItemsHeaders = (
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  const { include_services_dispensing_fees } = saleConfiguration;

  const headers = new Set<string>();

  const options = Object.entries(saleConfiguration)
    .filter(([key, value]) => value)
    .map(([key]) => key);

  const keys: string[] = [];

  // generate headers for common cases

  options.forEach((key) => {
    if (key.endsWith("_show_product")) {
      headers.add("Product");
    } else if (key.endsWith("_show_description")) {
      headers.add("Description");
    } else if (key.endsWith("_show_ear")) {
      headers.add("Ear");
    } else if (key.endsWith("_show_cpt_code")) {
      headers.add("CPT Code");
    } else if (key.endsWith("_show_manufacturer")) {
      headers.add("Manufacturer");
    } else if (key.endsWith("_show_model")) {
      headers.add("Model");
    } else if (key.endsWith("_show_color")) {
      headers.add("Color");
    } else if (key.endsWith("_show_battery")) {
      headers.add("Battery");
    } else if (key.endsWith("_show_serial")) {
      headers.add("Serial #");
    } else if (key.endsWith("_show_mfr_warranty_exp")) {
      headers.add("MFR Warranty Exp");
    } else if (key.endsWith("_show_lnd_warranty_exp")) {
      headers.add("L&D Warranty Exp");
    } else if (key.endsWith("_show_service_plan_exp")) {
      headers.add("Service Plan Exp");
    } else if (key.endsWith("_show_lnd_deductible")) {
      headers.add("L&D Deductible");
    } else if (key.endsWith("_show_price")) {
      headers.add("Price");
    } else if (key.endsWith("_show_quantity")) {
      headers.add("Qty");
    } else if (
      key.endsWith("_show_patient_resp") ||
      key.endsWith("_show_insurance_resp") ||
      key.endsWith("_show_managed_care_resp")
    ) {
      headers.add("Resp.");
    } else if (key.endsWith("_show_material")) {
      headers.add("Material");
    } else if (key.endsWith("_show_canal_length")) {
      headers.add("Canal Length");
    } else if (key.endsWith("_show_canal_venting")) {
      headers.add("Canal Venting");
    } else if (key.endsWith("_show_gain")) {
      headers.add("Gain");
    } else if (key.endsWith("_show_length")) {
      headers.add("Length");
    } else if (key.endsWith("_show_dome_type")) {
      headers.add("Dome Type");
    } else if (key.endsWith("_show_dome_size")) {
      headers.add("Dome Size");
    }

    keys.push(key);
  });

  const result = Array.from(headers);
  const keysString = keys.join(";");

  // adjust generated headers
  if (
    result.includes("Product") &&
    !(keysString.includes("_show_product") || include_services_dispensing_fees)
  ) {
    result.splice(result.indexOf("Product"), 1);
  }
  if (
    result.includes("Color") &&
    !keysString.includes("hearing_aids_") &&
    !keysString.includes("receivers_") &&
    !keysString.includes("batteries_")
  ) {
    result.splice(result.indexOf("Color"), 1);
  }
  if (result.includes("Battery") && !keysString.includes("hearing_aids_")) {
    result.splice(result.indexOf("Battery"), 1);
  }
  if (
    result.includes("Service Plan Exp") &&
    !keysString.includes("hearing_aids_")
  ) {
    result.splice(result.indexOf("Service Plan Exp"), 1);
  }
  if (result.includes("Material") && !keysString.includes("earmolds_")) {
    result.splice(result.indexOf("Material"), 1);
  }
  if (result.includes("Canal Length") && !keysString.includes("earmolds_")) {
    result.splice(result.indexOf("Canal Length"), 1);
  }
  if (result.includes("Canal Venting") && !keysString.includes("earmolds_")) {
    result.splice(result.indexOf("Canal Venting"), 1);
  }
  if (result.includes("Gain") && !keysString.includes("receivers_")) {
    result.splice(result.indexOf("Gain"), 1);
  }
  if (result.includes("Length") && !keysString.includes("receivers_")) {
    result.splice(result.indexOf("Length"), 1);
  }
  if (result.includes("Dome Type") && !keysString.includes("domes_")) {
    result.splice(result.indexOf("Dome Type"), 1);
  }
  if (result.includes("Dome Size") && !keysString.includes("domes_")) {
    result.splice(result.indexOf("Dome Size"), 1);
  }

  if (
    result.includes("Ear") &&
    !(
      keysString.includes("hearing_aids_") ||
      keysString.includes("earmolds_") ||
      keysString.includes("domes_") ||
      keysString.includes("receivers_") ||
      keysString.includes("tubes_")
    )
  ) {
    result.splice(result.indexOf("Ear"), 1);
  }

  result.sort((a, b) => {
    const indexA = HEADER_ITEMS_ORDER.indexOf(a);
    const indexB = HEADER_ITEMS_ORDER.indexOf(b);

    // If both are in the order array, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    // If only a is in the order array, it comes first
    if (indexA !== -1) {
      return -1;
    }
    // If only b is in the order array, it comes first
    if (indexB !== -1) {
      return 1;
    }
    // If neither is in the order array, maintain original order
    return 0;
  });

  return result;
};

export const getConfigurableTotals = (
  sale: Sale,
  saleConfiguration: Partial<SelectSaleConfigurationValues>,
  totalColumns: number
) => {
  const {
    total_show_discounts,
    total_show_services_dispensing_fees,
    total_tax,
    total_amount,
    total_patient_payments,
    total_insurance_payments,
    total_show_cost_of_ha,
    amount_due_from_insurance,
    amount_due_from_patient,
    balance_due
  } = saleConfiguration;
  const {
    patientPaymentDetails,
    totalDiscounts,
    totalTax,
    totalServices,
    total,
    balance,
    totalInsurancePayments,
    totalPatientPayments,
    amountDueFromInsurance,
    amountDueFromPatient,
    totalCostOfHearingAids
  } = getSaleTotals(sale);

  let result = "";

  let colspan = max([totalColumns - 1, 1]);

  if (total_show_services_dispensing_fees) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right; font-size:12px;">Services/Dispensing&nbsp;Fees</td>
    <td style="text-align: right; width: 120px; font-size:12px;">${formatMoneyValue(totalServices)}</td>
  </tr>`;
  }
  if (total_show_cost_of_ha) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right; font-size:12px;">Total Cost of Hearing Aids</td>
    <td style="text-align: right; width: 120px; font-size:12px;">${formatMoneyValue(totalCostOfHearingAids)}</td>
  </tr>`;
  }
  if (total_show_discounts) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right; font-size:12px;">Total Discounts</td>
    <td style="text-align: right; width: 120px; font-size:12px;">${formatMoneyValue(totalDiscounts)}</td>
  </tr>`;
  }

  if (total_tax) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right; font-size:12px;">Total Tax</td>
    <td style="text-align: right; width: 120px; font-size:12px;">${formatMoneyValue(totalTax)}</td>
  </tr>`;
  }

  if (total_amount) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right; font-size:12px;"><strong>Total Amount</strong></td>
    <td style="text-align: right; width: 120px; font-size:12px;">${formatMoneyValue(total)}</td>
  </tr>`;
  }

  if (total_patient_payments) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right; font-size:12px;">Total Patient Payments</td>
    <td style="text-align: right; width: 120px; font-size:12px;">${formatMoneyValue(totalPatientPayments)}</td>
  </tr>`;

    patientPaymentDetails.forEach(({ method, amount, detail }) => {
      const methodLower = method.toLowerCase();
      const detailLower = detail.toLowerCase();
      const detailStartsWithMethod =
        detailLower === methodLower ||
        detailLower.startsWith(`${methodLower} `);
      const labelText = (
        detail
          ? detailStartsWithMethod
            ? detail
            : `${method} ${detail}`
          : method
      ).trim();
      const label = escapeHtml(labelText);
      result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: left; font-size:12px;">${label}</td>
    <td style="text-align: right; width: 120px; font-size:12px;">${formatMoneyValue(amount)}</td>
  </tr>`;
    });
  }

  if (total_insurance_payments) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right; font-size:12px;">Total Insurance Payments</td>
    <td style="text-align: right; width: 120px; font-size:12px;">${formatMoneyValue(totalInsurancePayments)}</td>
  </tr>`;
  }

  if (amount_due_from_insurance) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right; font-size:12px;">Amount Due From Insurance</td>
    <td style="text-align: right; width: 120px; font-size:12px;">${formatMoneyValue(amountDueFromInsurance)}</td>
  </tr>`;
  }

  if (amount_due_from_patient) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right; font-size:12px;">Amount Due From Patient</td>
    <td style="text-align: right; width: 120px; font-size:12px;">${formatMoneyValue(amountDueFromPatient)}</td>
  </tr>`;
  }

  if (balance_due) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right; font-size:12px;"><strong>Balance Due</strong></td>
    <td style="text-align: right; width: 120px; font-size:12px;">${formatMoneyValue(balance, balance < 0)}</td>
  </tr>`;
  }

  return result;
};
